import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../utils/apiClient';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
  ArrowLeft, CheckCircle, ShoppingBag, Star, AlertCircle,
  FileText, Mic, Video, Package
} from 'lucide-react';

export default function MarketplaceDetail() {
  const { t } = useTranslation();
  const { skillPackageId } = useParams();
  const navigate = useNavigate();
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSkillDetail();
  }, [skillPackageId]);

  const fetchSkillDetail = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/marketplace/skills/${skillPackageId}`);
      setSkill(response.data);
    } catch (error) {
      console.error('Failed to fetch skill detail:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGetSkill = async () => {
    try {
      await apiClient.post(`/marketplace/skills/${skillPackageId}/claim`);
      toast.success(t('marketplace.skillAdded'));
      fetchSkillDetail();
    } catch (error) {
      console.error('Failed to get skill:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleInstall = async () => {
    try {
      // Install the skill directly
      await apiClient.post('/skills/install', {
        skillPackageId: skillPackageId,
        twinId: 'default-twin',
        approvalMode: 'assisted',
        allowedDataSources: {
          vault: true,
          textTraining: true
        }
      });
      
      toast.success('Skill installed successfully! You can now use it in the AI Assistant.');
      
      // Refresh skill details to show updated status
      await fetchSkillDetail();
    } catch (error) {
      console.error('Failed to install skill:', error);
      toast.error(error.response?.data?.detail || 'Failed to install skill');
    }
  };

  const getRiskBadgeColor = (risk) => {
    switch (risk) {
      case 'low': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'high': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading skill details...</p>
        </div>
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="p-12 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Skill Not Found</h2>
          <p className="text-gray-600 mb-6">The skill you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/personal/marketplace')}>
            Back to Marketplace
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="marketplace-detail-page">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate('/personal/marketplace')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Marketplace
      </Button>

      {/* Header Card */}
      <Card className="p-8">
        <div className="space-y-6">
          {/* Title and Badges */}
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{skill.name}</h1>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>by</span>
                  <Badge variant="outline">
                    {skill.publisherType === 'platform' ? '🏢 HumaTwin Platform' : '👤 Community Developer'}
                  </Badge>
                  {skill.isOwned && (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Owned
                    </Badge>
                  )}
                </div>
              </div>
              
              <Package className="w-12 h-12 text-primary" />
            </div>

            <div className="flex flex-wrap gap-2">
              <Badge className="text-sm">{skill.category}</Badge>
              <Badge className={getRiskBadgeColor(skill.riskLevel) + " text-sm"}>
                {skill.riskLevel} risk
              </Badge>
              <Badge variant="outline" className="text-sm">v{skill.version}</Badge>
            </div>
          </div>

          <Separator />

          {/* Description */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Description</h2>
            <p className="text-gray-700 leading-relaxed">{skill.description}</p>
          </div>

          {/* Requirements */}
          <div>
            <h2 className="text-lg font-semibold mb-3">Requirements</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className={`p-4 ${skill.requirements.text ? 'border-blue-500 bg-blue-50' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-3">
                  <FileText className={`w-6 h-6 ${skill.requirements.text ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium">Text Training</p>
                    <p className="text-sm text-gray-600">
                      {skill.requirements.text ? 'Required' : 'Not needed'}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className={`p-4 ${skill.requirements.voice ? 'border-purple-500 bg-purple-50' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-3">
                  <Mic className={`w-6 h-6 ${skill.requirements.voice ? 'text-purple-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium">Voice Training</p>
                    <p className="text-sm text-gray-600">
                      {skill.requirements.voice ? 'Required (Coming Soon)' : 'Not needed'}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className={`p-4 ${skill.requirements.video ? 'border-pink-500 bg-pink-50' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-3">
                  <Video className={`w-6 h-6 ${skill.requirements.video ? 'text-pink-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium">Video Training</p>
                    <p className="text-sm text-gray-600">
                      {skill.requirements.video ? 'Required (Coming Soon)' : 'Not needed'}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Capabilities */}
          {skill.capabilities && skill.capabilities.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3">Capabilities</h2>
              <div className="flex flex-wrap gap-2">
                {skill.capabilities.map((capability, idx) => (
                  <Badge key={idx} variant="outline" className="text-sm">
                    {capability}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {skill.tags && skill.tags.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {skill.tags.map((tag, idx) => (
                  <Badge key={idx} variant="secondary" className="text-sm">
                    #{tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center gap-6 text-sm text-gray-600 pt-4 border-t">
            <span className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" />
              {skill.installCount} installs
            </span>
            {skill.rating && (
              <span className="flex items-center gap-2">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                {skill.rating.toFixed(1)} rating
              </span>
            )}
            <span className="text-gray-400">•</span>
            <span>{skill.priceModel === 'free' ? 'Pilot' : 'Paid'}</span>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-4">
        {skill.isInstalled ? (
          <Button 
            onClick={() => navigate(`/personal/twin/skills?skillId=${skillPackageId}`)} 
            className="flex-1"
            data-testid="manage-skill-button"
          >
            Manage Skill
          </Button>
        ) : skill.isOwned ? (
          <Button 
            onClick={handleInstall} 
            className="flex-1"
            data-testid="install-skill-button"
          >
            Install to Twin
          </Button>
        ) : (
          <Button 
            onClick={handleGetSkill} 
            className="flex-1"
            data-testid="get-skill-button"
          >
            Get Skill
          </Button>
        )}
      </div>
    </div>
  );
}
